"use client"

import type React from "react"
import { createContext, useContext, useEffect, useMemo, useState } from "react"
import { LOCALE_COOKIE_NAME, LOCALE_STORAGE_KEY } from "@/lib/locale"
import { getLocaleDirection, getLocaleOption, resolveLocale, translate, type TranslationKey } from "@/lib/i18n"

type LanguageContextValue = {
  locale: string
  dir: "ltr" | "rtl"
  label: string
  nativeLabel: string
  setLocale: (nextLocale: string) => void
  t: (key: TranslationKey) => string
}

const LanguageContext = createContext<LanguageContextValue | null>(null)

function persistLocale(locale: string) {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  } catch {}
  try {
    document.cookie = `${LOCALE_COOKIE_NAME}=${encodeURIComponent(locale)}; path=/; max-age=31536000; samesite=lax`
  } catch {}
}

export function LanguageProvider({ children, initialLocale }: { children: React.ReactNode; initialLocale?: string }) {
  const [locale, setLocaleState] = useState(resolveLocale(initialLocale || "fr"))

  useEffect(() => {
    const saved = typeof window !== "undefined" ? localStorage.getItem(LOCALE_STORAGE_KEY) : null
    const cookieLocale = typeof document !== "undefined"
      ? document.cookie.split("; ").find((entry) => entry.startsWith(`${LOCALE_COOKIE_NAME}=`))?.split("=")[1]
      : null
    const nextLocale = resolveLocale(saved || cookieLocale || initialLocale || "fr")
    setLocaleState(nextLocale)
  }, [initialLocale])

  useEffect(() => {
    const nextDir = getLocaleDirection(locale)
    document.documentElement.lang = locale
    document.documentElement.dir = nextDir
    persistLocale(locale)
  }, [locale])

  const value = useMemo<LanguageContextValue>(() => {
    const option = getLocaleOption(locale)
    return {
      locale,
      dir: getLocaleDirection(locale),
      label: option.label,
      nativeLabel: option.nativeLabel,
      setLocale: (nextLocale: string) => setLocaleState(resolveLocale(nextLocale)),
      t: (key) => translate(locale, key),
    }
  }, [locale])

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

export function useLanguage() {
  const context = useContext(LanguageContext)
  if (!context) {
    // Fallback for SSR / hydration edge cases — return safe defaults (French)
    return {
      locale: "fr",
      dir: "ltr" as "ltr" | "rtl",
      label: "French",
      nativeLabel: "Français",
      setLocale: (_: string) => {},
      t: (key: TranslationKey) => translate("fr", key),
    }
  }
  return context
}
